/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface BrowseNode {
  id: string;
  name: string;
  isFolder: boolean;
  modifiedAt: string;
  sizeInBytes?: number;
  documentType: string;
  /** True when the caller may add children — i.e. it can be a destination. */
  canCreate: boolean;
}

export interface BrowsePage {
  nodes: BrowseNode[];
  total: number;
}

export interface FolderInfo {
  id: string;
  name: string;
  createdAt: string;
  modifiedAt: string;
  createdByName: string;
  /** Breadcrumb from Company Home down to this folder, inclusive. */
  crumbs: { id: string; name: string }[];
}

/**
 * Browsing, copy and move for the copy/move workspace (UI-08).
 *
 * All four operations are Alfresco's own: `/nodes/{id}/children` to browse,
 * `POST /nodes/{id}/copy`, `POST /nodes/{id}/move`, and `POST
 * /nodes/{parent}/children` to create a destination folder. Nothing is cached or
 * reconciled client-side — after an operation the caller re-reads both panes, so
 * what the screen shows is what the repository holds.
 */
@Injectable({ providedIn: 'root' })
export class CopyMoveService {
  private readonly api = inject(AlfrescoApiService);

  private call(path: string, method: string, body?: any, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, body ?? null, ['application/json'], ['application/json']) as Promise<any>
    );
  }

  /** One page of a folder's children, folders first. */
  children(folderId: string, skipCount = 0, maxItems = 100): Observable<BrowsePage> {
    return this.call(`/nodes/${folderId}/children`, 'GET', null, {
      skipCount,
      maxItems,
      include: 'properties,allowableOperations',
      orderBy: 'isFolder DESC,name ASC'
    }).pipe(
      map((res) => ({
        nodes: (res?.list?.entries ?? []).map((e: any) => this.toNode(e.entry)),
        total: res?.list?.pagination?.totalItems ?? 0
      }))
    );
  }

  /** Folder metadata plus its breadcrumb. */
  info(folderId: string): Observable<FolderInfo> {
    return this.call(`/nodes/${folderId}`, 'GET', null, { include: 'path' }).pipe(
      map((res) => {
        const entry = res?.entry ?? {};
        const elements: any[] = entry.path?.elements ?? [];
        const crumbs = elements
          .filter((element) => element.name !== 'Company Home' || elements.length === 1)
          .map((element) => ({ id: element.id, name: element.name }));

        return {
          id: entry.id,
          name: entry.name,
          createdAt: entry.createdAt,
          modifiedAt: entry.modifiedAt,
          createdByName: entry.createdByUser?.displayName ?? entry.createdByUser?.id ?? '',
          crumbs: [...crumbs, { id: entry.id, name: entry.name }]
        };
      })
    );
  }

  /** Copies one node into a folder. */
  copy(nodeId: string, targetParentId: string): Observable<any> {
    return this.call(`/nodes/${nodeId}/copy`, 'POST', { targetParentId });
  }

  /** Moves one node into a folder. */
  move(nodeId: string, targetParentId: string): Observable<any> {
    return this.call(`/nodes/${nodeId}/move`, 'POST', { targetParentId });
  }

  /** Creates a folder inside another. */
  createFolder(parentId: string, name: string): Observable<any> {
    return this.call(`/nodes/${parentId}/children`, 'POST', { name, nodeType: 'cm:folder' });
  }

  private toNode(entry: any): BrowseNode {
    const operations: string[] = entry.allowableOperations ?? [];
    return {
      id: entry.id,
      name: entry.name,
      isFolder: !!entry.isFolder,
      modifiedAt: entry.modifiedAt,
      sizeInBytes: entry.content?.sizeInBytes,
      documentType: entry.properties?.['dms:documentType'] ?? '',
      canCreate: !!entry.isFolder && operations.includes('create')
    };
  }
}
